import Router, { Route } from "vue-router";
import { RootState } from "@/store/store";
import { Url } from "@/commons/constants/Url";
import { Functions, ReservationsFunction, SendMessagesFunction, Role, SendDocumentsFunction } from "@/commons/services/Authorization";
import CommonService from "@/commons/services/CommonService";
import FacilitiesService, { FacilityGetModel, PermittedFunctions } from "@/commons/services/Facilities";
import LoginUser from "@/store/LoginUser";
import { PlanType } from "@/commons/services/Plans";
import { WaiterServicePlanType } from "./commons/services/WaiterServicePlans";

/**
 * ## Vueルーターに画面遷移前処理を登録する
 *
 * - 画面遷移前のルーターによるフック
 * - ログイン認証前はログイン画面へ遷移するようにルーターでフックする
 * - 機能の使用可否が他ユーザーに変更される可能性があるため、ログイン認証後は画面遷移するたびに施設情報を更新する
 * - router.tsの各ルーティング情報のmetaプロパティに「isPublic=true」がないものはログイン済みかチェックする
 * - 機能の使用可否チェックもここで行い、使用不可の場合はNotFound画面へ遷移させる
 */
export function registerRouterBeforeEach(router: Router, store: any) {
  router.beforeEach(async (to: Route, from: Route, next: any) => {
    const state: RootState = store.state;

    /**
     * 最新の施設情報を取得してstoreに保持する
     * 現在パスがログイン画面の場合はログイン時にstoreに保持する処理を実施しているので処理不要
     * ログイン状態が「ログイン済み」でなければログイン画面へ遷移するので処理不要
     */
    try {
      if (state.loginUser.loggedIn) {
        const commonService: CommonService = new CommonService();
        const facilitiesService: FacilitiesService = new FacilitiesService(commonService);
        const facility: FacilityGetModel = await facilitiesService.get(state.loginUser as LoginUser);
        state.loginUser.facility = facility;
      }
    } catch (error) {
      // ここでエラーになった場合、ハンドリングせずに無視する
      // 施設情報取得でエラーになるパターンは認証切れorシステムエラーであり、その場合は後続処理(画面表示処理等)でエラーになる
      // それらは共通エラーハンドラーで適切に処理されるのでここで何もしなくても問題ない
    }

    /**
     * 遷移先とサービス利用可否の組み合わせによる遷移可否チェック
     * 遷移不可の機能へアクセスしようとした場合、404画面へ遷移させる
     */
    if (!checkUseService(to.fullPath, state.loginUser.facility, state.loginUser.loginUser.permissions.functions)) {
      next({
        path: Url.NotFound
      });
      return;
    }

    /**
     * 遷移先とプラン種別の組み合わせによる遷移可否チェック
     * 遷移不可の機能へアクセスしようとした場合、404画面へ遷移させる
     */
    if (!checkPlan(to.fullPath, state.loginUser.facility)) {
      next({
        path: Url.NotFound
      });
      return;
    }

    /**
     * 機能の使用可否チェック
     * 使用不可の機能へアクセスしようとした場合、404画面へ遷移させる
     */
    if (!checkPermittedFunction(to.fullPath, state.loginUser.facility)) {
      next({
        path: Url.NotFound
      });
      return;
    }

    /**
     * ユーザー権限チェック
     * 遷移不可の機能へアクセスしようとした場合、404画面へ遷移させる
     */
    if (!checkRole(to.fullPath, state.loginUser.loginUser.role)) {
      next({
        path: Url.NotFound
      });
      return;
    }

    /**
     * ユーザー単位の機能使用可否チェック
     * 遷移不可の機能へアクセスしようとした場合、404画面へ遷移させる
     */
    if (!checkUserUseFunction(to.fullPath, state.loginUser.loginUser.permissions.functions)) {
      next({
        path: Url.NotFound
      });
      return;
    }

    /**
     * ユーザー単位の機能使用可否チェック(面会予約)
     * 面会予約の詳細機能チェックをし、遷移不可の機能へアクセスしようとした場合、404画面へ遷移させる
     */
    if (!checkUserUseReservationsFunction(to.fullPath, state.loginUser.loginUser.permissions.reservationsFunction)) {
      next({
        path: Url.NotFound
      });
      return;
    }

    /**
     * ユーザー単位の機能使用可否チェック(メッセージ送信)
     * メッセージ送信の詳細機能チェックをし、遷移不可の機能へアクセスしようとした場合、404画面へ遷移させる
     */
    if (!checkUserUseSendMessagesFunction(to.fullPath, state.loginUser.loginUser.permissions.sendMessagesFunction)) {
      next({
        path: Url.NotFound
      });
      return;
    }

    /**
     * ユーザー単位の機能使用可否チェック(文書送付)
     * 文書送付の詳細機能チェックをし、遷移不可の機能へアクセスしようとした場合、404画面へ遷移させる
     */
    if (!checkUserUseSendDocumentsFunction(to.fullPath, state.loginUser.loginUser.permissions.sendDocumentsFunction)) {
      next({
        path: Url.NotFound
      });
      return;
    }

    /**
     * ログイン済みか認証チェック
     * 認証不要ページ、または認証が必要ページだがログイン認証していない場合はログイン画面へ遷移させる
     */
    if (to.matched.some((record) => !record.meta.isPublic) && !state.loginUser.loggedIn) {
      next({
        path: Url.Root,
        query: { redirect: to.fullPath }
      });
      return;
    }

    // チェックでNGで無ければ指定された画面へ遷移する
    next();

    return;
  });
}

/**
 * ### 遷移先とサービス利用可否の組み合わせに応じた画面遷移可否を返す
 *
 * @param path 機能を含んだパス
 * @param facility 施設情報
 *
 * @returns 遷移可否(true:可能、false：不可)
 */
function checkUseService(path: string, facility: FacilityGetModel, functions: Functions): boolean {
  // 家族連絡サービスが使用不可の場合は家族連絡サービスのURLには遷移不可
  if (facility.useFamilyService == false) {
    /** サイドメニュー */
    // ホーム
    if (0 == path.indexOf("/home-for-families")) {
      return false;
    }
    // メッセージ送信
    if (0 == path.indexOf("/send-messages")) {
      return false;
    }
    // 文書送付
    if (0 == path.indexOf("/send-document")) {
      return false;
    }
    // 受信メッセージ
    if (0 == path.indexOf("/receive-message")) {
      return false;
    }
    // 面会予約
    if (0 == path.indexOf("/reservation")) {
      return false;
    }
    // 外出外泊届け
    if (0 == path.indexOf("/outings")) {
      return false;
    }
    // イベント
    if (0 == path.indexOf("/event")) {
      return false;
    }
    // 写真印刷依頼
    if (0 == path.indexOf("/picture")) {
      return false;
    }
    // 介護記録
    if (0 == path.indexOf("/careimage")) {
      return false;
    }
    // 空床スケジュール
    if (0 == path.indexOf("/room-schedule")) {
      return false;
    }
    // アンケート
    if (0 == path.indexOf("/questionnaires")) {
      return false;
    }
    // 安否確認
    if (0 == path.indexOf("/safety-confirm")) {
      return false;
    }
    // 家族の使用状況
    if (0 == path.indexOf("/families-message")) {
      return false;
    }

    /** トップメニュー・お問い合わせ */
    /** トップメニュー・お知らせ */
    /** トップメニュー・利用者等管理 */
    // 利用者
    if (0 == path.indexOf("/resident")) {
      return false;
    }
    // 家族ユーザー
    if (0 == path.indexOf("/family-user")) {
      return false;
    }
    // 施設関係者グループ
    if (0 == path.indexOf("/other-user-group")) {
      return false;
    }
    // 宛先グループ
    if (0 == path.indexOf("/send-message-groups")) {
      return false;
    }

    /** トップメニュー・各種設定 */
    // 部屋
    if (0 == path.indexOf("/room")) {
      return false;
    }
    // 家族の使用機能
    if (0 == path.indexOf("/permitted-function")) {
      return false;
    }
    // 家族の使用機能(個別設定)
    if (0 == path.indexOf("/family-permitted-function")) {
      return false;
    }
    // 施設関係者の使用機能
    if (0 == path.indexOf("/other-user-permitted-function")) {
      return false;
    }
  }

  // 入所者募集・待機者管理サービスが使用不可の場合は入所者募集・待機者管理サービスのURLには遷移不可
  if (facility.useWaiterService == false || functions.useWaiterService == false) {
    // ホーム
    if (0 == path.indexOf("/home-for-waiters")) {
      return false;
    }
    // 入所申込管理
    if (0 == path.indexOf("/waiters")) {
      return false;
    }
    // 待機者メッセージ送信
    if (0 == path.indexOf("/waiter-send-messages")) {
      return false;
    }
    // 待機者受信メッセージ
    if (0 == path.indexOf("/waiter-receive-message")) {
      return false;
    }
    // フォロワー
    if (0 == path.indexOf("/followers")) {
      return false;
    }
    // 施設見学
    if (0 == path.indexOf("/inspection-reservations")) {
      return false;
    }
    // 入所募集問い合わせ
    if (0 == path.indexOf("/recruitment-contacts")) {
      return false;
    }
  }

  // 他事業所サービスが使用不可、他事業所サービス権限が使用不可の場合は他事業所連携サービスのURLには遷移不可
  if (facility.useOtherOfficeConnectionService == false || functions.useOtherOfficeConnectionService == false) {
    // ホーム
    if (0 == path.indexOf("/home-for-other-office")) {
      return false;
    }
    // ホーム(他事業所連携情報編集)
    if (0 == path.indexOf("/other-office-connection/edit")) {
      return false;
    }
    // 他事業所連携メッセージ
    if (0 == path.indexOf("/other-office-send-messages")) {
      return false;
    }
    // 他事業所連携空床情報
    if (0 == path.indexOf("/other-office-room-schedules")) {
      return false;
    }
  }

  // 家族連絡サービス、他事業所連携サービスが使用不可の場合は遷移不可
  if (facility.useFamilyService == false && (facility.useOtherOfficeConnectionService == false || functions.useOtherOfficeConnectionService == false)) {
    // 通知設定
    if (0 == path.indexOf("/notification-setting")) {
      return false;
    }
  }

  return true;
}

/**
 * ### プラン種別に応じた画面遷移可否を返す
 *
 * @param path 機能を含んだパス
 * @param facility 施設情報
 *
 * @returns 遷移可否(true:可能、false：不可)
 */
function checkPlan(path: string, facility: FacilityGetModel): boolean {
  const planType: PlanType = facility.planType;
  const waiterPlanType: WaiterServicePlanType = facility.waiterServicePlanType;
  const permitted: PermittedFunctions = facility.permittedFunctions;

  // 家族連絡サービス契約時の遷移不可制限
  if (facility.useFamilyService) {
    // フリープラン・ライトプラン・エントリープラン共通の遷移不可制限
    if (planType == PlanType.FREE || planType == PlanType.LIGHT || planType == PlanType.ENTRY) {
      if (0 == path.indexOf("/questionnaires")) {
        return false;
      }
      if (0 == path.indexOf("/safety-confirm")) {
        return false;
      }
      if (0 == path.indexOf("/send-document")) {
        return false;
      }
      if (0 == path.indexOf("/room-schedule")) {
        return false;
      }
    }

    // フリープラン・ライトプラン共通の遷移不可制限
    if (planType == PlanType.FREE || planType == PlanType.LIGHT) {
      // PC
      if (0 == path.indexOf("/other-user-group")) {
        return false;
      }
      if (0 == path.indexOf("/careimage")) {
        return false;
      }
      if (0 == path.indexOf("/picture")) {
        return false;
      }
      if (0 == path.indexOf("/event")) {
        return false;
      }
      if (0 == path.indexOf("/receive-message") && permitted.opinion == false) {
        return false;
      }
      if (0 == path.indexOf("/other-user-permitted-function")) {
        return false;
      }
      if (0 == path.indexOf("/questionnaires")) {
        return false;
      }

      // モバイル
      if (0 == path.indexOf("/mobile/message/other-user")) {
        return false;
      }
      if (0 == path.indexOf("/mobile/receive-message/other-user")) {
        return false;
      }
    }

    // フリープランの遷移不可制限
    if (planType == PlanType.FREE) {
      if (0 == path.indexOf("/resident/group")) {
        return false;
      }
      if (0 == path.indexOf("/send-message-groups")) {
        return false;
      }
      if (0 == path.indexOf("/family-permitted-function")) {
        return false;
      }
      if (0 == path.indexOf("/outings")) {
        return false;
      }
      // 他事業所連携サービスが使用不可の場合、家族連絡サービスがフリーだと遷移させない
      if (!facility.useOtherOfficeConnectionService) {
        if (0 == path.indexOf("/notification-setting/edit")) {
          return false;
        }
      }
    }
  }

  // 待機者管理サービス契約時の遷移不可制限
  if (facility.useWaiterService) {
    // フリープランの遷移不可制限
    if (waiterPlanType == WaiterServicePlanType.FREE) {
      // PC
      if (0 == path.indexOf("/waiter-send-messages")) {
        return false;
      }
      if (0 == path.indexOf("/waiter-receive-message")) {
        return false;
      }
      if (0 == path.indexOf("/followers")) {
        return false;
      }
    }
  }

  // サービス共通で使用しているURLの遷移不可制限
  // 他事業所連携サービスが使用可能な場合はカレンダー・履歴は表示可能
  if (facility.useOtherOfficeConnectionService) {
    if (0 == path.indexOf("/report")) {
      return true;
    }
  }
  // 他事業所連携サービスが使用不可の場合、家族連絡サービス、待機者サービスの組み合わせで遷移不可かを判断する
  if (facility.useFamilyService && facility.useWaiterService) {
    if (planType == PlanType.FREE && waiterPlanType == WaiterServicePlanType.FREE) {
      if (0 == path.indexOf("/account/standard")) {
        return false;
      }
      if (0 == path.indexOf("/report")) {
        return false;
      }
    }
  }
  if (!facility.useFamilyService && facility.useWaiterService) {
    if (waiterPlanType == WaiterServicePlanType.FREE) {
      if (0 == path.indexOf("/account/standard")) {
        return false;
      }
      if (0 == path.indexOf("/report")) {
        return false;
      }
    }
  }
  if (facility.useFamilyService && !facility.useWaiterService) {
    if (planType == PlanType.FREE) {
      if (0 == path.indexOf("/account/standard")) {
        return false;
      }
      if (0 == path.indexOf("/report")) {
        return false;
      }
    }
  }

  // その他は一律遷移可能を返す
  return true;
}

/**
 * ### 機能使用可否に応じた画面遷移可否を返す
 *
 * @param path 機能名を含んだパス
 * @param facility 施設情報
 *
 * @returns 遷移可否(true:可能、false：不可)
 */
function checkPermittedFunction(path: string, facility: FacilityGetModel): boolean {
  // 介護記録
  if (0 == path.indexOf("/careimage")) {
    return facility.permittedFunctions.careimage;
  }
  // 写真印刷依頼
  else if (0 == path.indexOf("/picture")) {
    return facility.permittedFunctions.picture;
  }
  // イベント情報
  else if (0 == path.indexOf("/event")) {
    return facility.permittedFunctions.event;
  }
  // 面会予約
  else if (0 == path.indexOf("/reservation")) {
    const permitted: PermittedFunctions = facility.permittedFunctions;
    if (permitted.reservation == false && permitted.onlineReservation == false) {
      return false;
    } else {
      return true;
    }
  }
  // 外出外泊届け
  else if (0 == path.indexOf("/outings")) {
    return facility.permittedFunctions.outing;
  }

  // その他は一律遷移可能を返す
  return true;
}

/**
 * ### ユーザーの権限に応じた画面遷移可否を返す
 *
 * @param path 機能名を含んだパス
 * @param role ユーザーの権限
 *
 * @returns 遷移可否(true:可能、false：不可)
 */
function checkRole(path: string, role: Role) {
  // 一般ユーザー以外はどの画面へ遷移してもOKなので即trueを返す
  if (role != Role.STANDARD_USER) {
    return true;
  }

  // 一般ユーザー管理
  if (0 == path.indexOf("/account/standard")) {
    return false;
  }
  // プラン
  else if (0 == path.indexOf("/plan")) {
    return false;
  }
  // 解約
  else if (0 == path.indexOf("/terminate")) {
    return false;
  }
  // 利用者一括登録
  else if (0 == path.indexOf("/resident/multiple/register")) {
    return false;
  }

  // その他は一律遷移可能を返す
  return true;
}

/**
 * ### ユーザー単位の機能使用可否に応じた画面遷移可否を返す
 *
 * @param path 機能名を含んだパス
 * @param functions ユーザー単位の機能使用可否
 */
function checkUserUseFunction(path: string, functions: Functions) {
  /**
   * PC・モバイル共通
   */
  // 施設編集
  if (0 == path.indexOf("/facility/edit")) {
    return functions.facilitiesFunction;
  }
  // 部屋情報
  if (0 == path.indexOf("/room/register") || 0 == path.indexOf("/room/multiple/register") || 0 == path.indexOf("/room/edit")) {
    return functions.roomsFunction;
  }
  // 家族の使用機能（変更）
  if (0 == path.indexOf("/permitted-function/edit")) {
    return functions.useFamilyFunctionsFunction;
  }
  // 家族の使用機能（個別設定）
  if (0 == path.indexOf("/family-permitted-function")) {
    return functions.individuallyUseFamilyFunctionsFunction;
  }
  // 施設関係者の使用機能（変更）
  if (0 == path.indexOf("/other-user-permitted-function/edit")) {
    return functions.useOtherUserFunctionsFunction;
  }
  // 利用者
  if (0 == path.indexOf("/resident")) {
    return functions.residentsFunction;
  }
  // 家族ユーザー
  else if (0 == path.indexOf("/family")) {
    return functions.familiesFunction;
  }
  // 施設関係者
  else if (0 == path.indexOf("/other-user-group")) {
    return functions.otherUserGroupsFunction;
  }
  // 宛先グループ
  if (0 == path.indexOf("/send-message-groups/register") || 0 == path.indexOf("/send-message-groups/edit") || 0 == path.indexOf("/send-message-groups/member/edit")) {
    return functions.roomsFunction;
  }
  // 介護記録
  else if (0 == path.indexOf("/careimage")) {
    return functions.careimagesFunction;
  }
  // 写真印刷依頼
  else if (0 == path.indexOf("/picture")) {
    return functions.picturesFunction;
  }
  // イベント情報
  else if (0 == path.indexOf("/event")) {
    return functions.eventsFunction;
  }
  // 面会予約
  else if (0 == path.indexOf("/reservation") || 0 == path.indexOf("/mobile/reservation")) {
    return functions.reservationsFunction;
  }
  // 外出外泊届け
  else if (0 == path.indexOf("/outings")) {
    return functions.outingsFunction;
  }
  // メッセージ送信(モバイルでは新規送信及びメッセージ履歴)
  else if (0 == path.indexOf("/send-messages") || 0 == path.indexOf("/mobile/message")) {
    return functions.sendMessagesFunction;
  }
  // 文書送付
  else if (0 == path.indexOf("/send-document")) {
    return functions.sendDocumentsFunction;
  }
  // 受信メッセージ
  else if (0 == path.indexOf("/receive-message") || 0 == path.indexOf("/mobile/receive-message")) {
    return functions.receiveMessagesFunction;
  }
  // 家族の使用状況
  else if (0 == path.indexOf("/families-message")) {
    return functions.familyMessagesFunction;
  }
  // 活動履歴
  else if (0 == path.indexOf("/report")) {
    return functions.reportsFunction;
  }
  // アンケート
  else if (0 == path.indexOf("/questionnaires")) {
    return functions.questionnairesFunction;
  }
  // 安否確認
  else if (0 == path.indexOf("/safety-confirm") || 0 == path.indexOf("/mobile/safety-confirm")) {
    return functions.safetyConfirmationFunction;
  }
  // 空床スケジュール
  else if (0 == path.indexOf("/room-schedule")) {
    return functions.roomSchedulesFunction;
  }
  // 待機者管理
  else if (0 == path.indexOf("/waiters")) {
    return functions.waitersFunction;
  }
  // 待機者メッセージ送信
  else if (0 == path.indexOf("/waiter-send-messages")) {
    return functions.waiterSendMessagesFunction;
  }
  // 待機者受信メッセージ
  else if (0 == path.indexOf("/waiter-receive-message")) {
    return functions.waiterReceiveMessagesFunction;
  }
  // フォロワー
  else if (0 == path.indexOf("/followers")) {
    return functions.waiterFollowersFunction;
  }
  // 施設見学
  else if (0 == path.indexOf("/inspection-reservations")) {
    return functions.inspectionReservationsFunction;
  }
  // 入所募集お問い合わせ
  else if (0 == path.indexOf("/recruitment-contacts")) {
    return functions.recruitmentContactsFunction;
  }
  // 他事業所連携編集（トップから遷移するので、トップの権限で管理する）
  else if (0 == path.indexOf("/other-office-connection/edit")) {
    return functions.otherOfficeTopPageFunction;
  }
  // 他事業所連携メッセージ
  else if (0 == path.indexOf("/other-office-send-messages")) {
    return functions.otherOfficeMessagesFunction;
  }
  // 他事業所連携空床情報
  else if (0 == path.indexOf("/other-office-room-schedules")) {
    return functions.otherOfficeRoomSchedulesFunction;
  }
  /**
   * モバイルのみ
   */
  // 新着メッセージ
  else if (0 == path.indexOf("/mobile/new-arrival-messages")) {
    return functions.sendMessagesFunction || functions.receiveMessagesFunction;
  }

  // その他は一律遷移可能を返す
  return true;
}

/**
 * ### ユーザー単位の機能使用可否(面会予約)に応じた画面遷移可否を返す
 *
 * - PCでは各専用画面が存在するので、URLによって機能使用可否を返す
 * - 専用画面が無い機能については機能使用可否を返す必要はない
 * - モバイルについては専用画面がないので機能使用可否を返す必要はない
 *
 * @param path 機能名を含んだパス
 * @param functions ユーザー単位の機能使用可否(面会予約)
 */
function checkUserUseReservationsFunction(path: string, reservationfunctions: ReservationsFunction) {
  // 面会予約の基本設定
  if (0 == path.indexOf("/reservation/settings")) {
    return reservationfunctions.useReservationSettings || reservationfunctions.useOnlineReservationSettings;
  }
  // 訪問面会日時の設定
  if (0 == path.indexOf("/reservation/schedule")) {
    return reservationfunctions.useReservationSchedules || reservationfunctions.useOnlineReservationSchedules || reservationfunctions.useReserveInstead;
  }

  // その他は一律遷移可能を返す
  return true;
}

/**
 * ### ユーザー単位の機能使用可否(メッセージ送信)に応じた画面遷移可否を返す
 *
 * - PCでは各専用画面が存在するので、URLによって機能使用可否を返す
 * - 専用画面が無い機能については機能使用可否を返す必要はない
 * - モバイルについては専用画面がないので機能使用可否を返す必要はない
 *
 * @param path 機能名を含んだパス
 * @param functions ユーザー単位の機能使用可否(メッセージ送信)
 */
function checkUserUseSendMessagesFunction(path: string, sendMessagesFunction: SendMessagesFunction) {
  // メッセージ送信の基本設定
  if (0 == path.indexOf("/send-messages/setting-edit")) {
    return sendMessagesFunction.useBasicSetting;
  }
  // メッセージ送信のテンプレート
  if (0 == path.indexOf("/send-messages/template")) {
    return sendMessagesFunction.useTemplate;
  }

  // モバイル：メッセージ登録入力、メッセージ登録入力確認、メッセージ登録完了
  if (0 == path.indexOf("/mobile/message/register")) {
    return sendMessagesFunction.useSendAndReply;
  }

  // その他は一律遷移可能を返す
  return true;
}

/**
 * ### ユーザー単位の機能使用可否(文書送付)に応じた画面遷移可否を返す
 *
 * - PCでは各専用画面が存在するので、URLによって機能使用可否を返す
 * - 専用画面が無い機能については機能使用可否を返す必要はない
 * - モバイルについては専用画面がないので機能使用可否を返す必要はない
 *
 * @param path 機能名を含んだパス
 * @param functions ユーザー単位の機能使用可否(メッセージ送信)
 */
function checkUserUseSendDocumentsFunction(path: string, sendDocumentsFunction: SendDocumentsFunction) {
  // 文書種類の編集
  if (0 == path.indexOf("/send-document/category/edit")) {
    return sendDocumentsFunction.useEdit;
  }
  // テンプレート
  if (0 == path.indexOf("/send-document/template")) {
    return sendDocumentsFunction.useTemplate;
  }

  // その他は一律遷移可能を返す
  return true;
}
